import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import MaskedReveal from "@/components/MaskedReveal";
import FadeUp from "@/components/FadeUp";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEO from "@/components/SEO";
import JsonLd from "@/components/JsonLd";
import NAPBlock from "@/components/NAPBlock";
import { CITIES, getCity } from "@/data/cities";
import { getCondition } from "@/data/care";
import {
  breadcrumbListJsonLd,
  medicalWebPageJsonLd,
  localBusinessJsonLd,
} from "@/data/schema";
import { WHATSAPP_LINK } from "@/lib/api";

// Google Maps embed — uses GBP-linked search so the pin lands on Dr Tegbir Sidhu's listing.
const MAP_EMBED = "https://www.google.com/maps?q=Dr+Tegbir+Sidhu+Max+Mohali&output=embed";

export default function City() {
  const { slug } = useParams();
  const city = getCity(slug);
  if (!city) return <Navigate to="/cities" replace />;
  const conds = (city.commonConditions || []).map(getCondition).filter(Boolean);
  const others = CITIES.filter((c) => c.slug !== city.slug).slice(0, 6);

  return (
    <div data-testid={`city-page-${city.slug}`} className="pt-28 md:pt-36 pb-28">
      <SEO
        title={`${city.name} — Vascular & Endovascular IR at Max Mohali · Dr Tegbir Vascular`}
        description={`${city.lede} ${city.distance} to the clinic.`}
        keywords={[`vascular treatment ${city.name.toLowerCase()}`, `interventional radiology ${city.name.toLowerCase()}`, `vascular specialist near ${city.name.toLowerCase()}`, "IR mohali"]}
      />
      <JsonLd data={[
        medicalWebPageJsonLd({ name: `${city.name} — Regional IR referrals`, description: city.lede, url: `/cities/${city.slug}` }),
        localBusinessJsonLd({ areaServed: { "@type": "City", "name": city.name, "address": { "@type": "PostalAddress", "addressRegion": city.region, "addressCountry": "IN" } } }),
        breadcrumbListJsonLd([
          { to: "/", label: "Home" },
          { to: "/cities", label: "Regions" },
          { label: city.name },
        ]),
      ]}/>

      <div className="mx-auto max-w-[1440px] px-6 md:px-10">
        <FadeUp>
          <Breadcrumbs trail={[{ to: "/", label: "Home" }, { to: "/cities", label: "Regions" }, { label: city.name }]} />
        </FadeUp>

        {/* Hero */}
        <div className="mt-12 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-8">
            <FadeUp>
              <div className="kbd">{city.region} · {city.tag}</div>
            </FadeUp>
            <h1 className="mt-8 font-serif text-6xl md:text-[7.5vw] leading-[0.94] tracking-tightest">
              <MaskedReveal text={`From ${city.name},`} stagger={0.05}/>
              <MaskedReveal text={"to the"} delay={0.15}/>
              <MaskedReveal text={"IR suite."} delay={0.28} className="italic font-normal"/>
            </h1>
            <FadeUp className="mt-10" delay={0.4}>
              <p className="text-xl md:text-2xl font-serif italic max-w-2xl leading-snug">"{city.lede}"</p>
              <p className="mt-8 text-lg text-ink-muted leading-relaxed max-w-2xl">{city.context}</p>
            </FadeUp>
          </div>

          <FadeUp className="col-span-12 md:col-span-4 md:pl-8" delay={0.2}>
            <div className="hairline bg-white/70 p-6">
              <div className="flex items-center gap-2 kbd">
                <MapPin size={12}/> Distance
              </div>
              <div className="mt-2 font-serif text-3xl tracking-tightest">{city.distance}</div>
              <div className="mt-6 pt-6 border-t border-black/10">
                <div className="kbd">Fastest first step</div>
                <a target="_blank" rel="noreferrer" href={WHATSAPP_LINK(`Hello Dr Sidhu, I'm writing from ${city.name}. I would like to enquire about a consultation.`)} data-testid={`city-whatsapp-${city.slug}`} className="mt-3 btn-pill-inv w-full justify-center">
                  WhatsApp <ArrowUpRight size={14}/>
                </a>
                <Link to="/contact" data-testid={`city-form-${city.slug}`} className="mt-3 btn-pill w-full justify-center">
                  Or send the form
                </Link>
              </div>
            </div>
          </FadeUp>
        </div>

        {/* Transit */}
        <div className="mt-32">
          <FadeUp>
            <div className="kbd">— Getting here</div>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tightest">How patients from {city.name} travel.</h2>
          </FadeUp>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
            {city.transit.map((t, i) => (
              <FadeUp key={i} delay={i * 0.05}>
                <div className="hairline p-5 bg-white/60 h-full">
                  <div className="font-mono text-sm text-crimson">0{i+1}</div>
                  <p className="mt-3 text-ink leading-relaxed">{t}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>

        {/* Common conditions */}
        {conds.length > 0 && (
          <div className="mt-32">
            <FadeUp>
              <div className="grid grid-cols-12 gap-6 items-end mb-12">
                <div className="col-span-12 md:col-span-8">
                  <div className="kbd">— What patients from {city.name} ask about most</div>
                  <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tightest">Common referrals</h2>
                </div>
                <div className="col-span-12 md:col-span-4 md:text-right">
                  <Link to="/care" className="kbd hover:text-ink">All conditions →</Link>
                </div>
              </div>
            </FadeUp>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {conds.map((cond) => (
                <Link key={cond.slug} to={`/conditions/${cond.slug}`} data-testid={`city-condition-${cond.slug}`} className="group hairline p-5 bg-white/60 hover:bg-white transition-colors">
                  <div className="kbd">Condition</div>
                  <h3 className="mt-2 font-serif text-2xl tracking-tightest group-hover:text-crimson transition-colors">{cond.name}</h3>
                  <p className="mt-2 text-sm text-ink-muted leading-relaxed">{cond.oneLiner}</p>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Referral notes */}
        {city.referralNotes && (
          <FadeUp className="mt-24">
            <div className="hairline p-6 md:p-8 bg-white/60">
              <div className="kbd">— Referral notes</div>
              <p className="mt-4 text-lg leading-relaxed max-w-3xl">{city.referralNotes}</p>
            </div>
          </FadeUp>
        )}

        {/* Map + NAP */}
        <div className="mt-32">
          <FadeUp>
            <div className="kbd">— The clinic</div>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl tracking-tightest">Max Super Speciality Hospital, Mohali.</h2>
          </FadeUp>
          <div className="mt-10 grid grid-cols-12 gap-6">
            <FadeUp className="col-span-12 md:col-span-7" delay={0.1}>
              <div className="aspect-[4/3] overflow-hidden hairline">
                <iframe
                  title="Max Super Speciality Hospital, Mohali"
                  src={MAP_EMBED}
                  className="w-full h-full"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  data-testid="city-map-embed"
                />
              </div>
            </FadeUp>
            <FadeUp className="col-span-12 md:col-span-5" delay={0.2}>
              <NAPBlock/>
            </FadeUp>
          </div>
        </div>

        {/* Other regions */}
        <div className="mt-32">
          <FadeUp>
            <div className="hairline-t pt-12">
              <div className="kbd">— Other regions</div>
              <div className="mt-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {others.map((o) => (
                  <Link key={o.slug} to={`/cities/${o.slug}`} className="group hairline p-4 bg-white/60 hover:bg-white transition-colors">
                    <div className="kbd">{o.region}</div>
                    <div className="mt-2 font-serif text-lg tracking-tightest group-hover:text-crimson">{o.name}</div>
                  </Link>
                ))}
              </div>
            </div>
          </FadeUp>
        </div>
      </div>
    </div>
  );
}
